import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { IConfirmationDialogBodyProps } from "./types";
import { getResolvedTestId } from "./utils";

export const ConfirmationDialogBody = (
  props: IConfirmationDialogBodyProps,
): JSX.Element => {
  const { content, actions, cancelRef, testIdPrefix, classNames } = props;
  const mergedClassNames = {
    title: "text-center text-2xl mb-2",
    description: "text-center text-base break-words",
    footer: "flex items-center mt-2",
    cancelButton: "grow basis-1/2 shrink-0",
    confirmButton: "grow basis-1/2 shrink-0",
    ...classNames,
  };
  const titleTestId = getResolvedTestId({
    testIdPrefix,
    suffix: "title",
  });
  const descriptionTestId = getResolvedTestId({
    testIdPrefix,
    suffix: "description",
  });
  const cancelButtonTestId = getResolvedTestId({
    testIdPrefix,
    suffix: "cancel-button",
  });
  const confirmButtonTestId = getResolvedTestId({
    testIdPrefix,
    suffix: "confirm-button",
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle
          className={mergedClassNames.title}
          data-testid={titleTestId}
        >
          {content.title}
        </DialogTitle>
        {content.description && (
          <DialogDescription
            className={mergedClassNames.description}
            data-testid={descriptionTestId}
          >
            {content.description}
          </DialogDescription>
        )}
      </DialogHeader>
      <DialogFooter className={mergedClassNames.footer}>
        <DialogClose asChild>
          <Button
            ref={cancelRef}
            variant="outline"
            className={mergedClassNames.cancelButton}
            onClick={actions.onCancel}
            data-testid={cancelButtonTestId}
          >
            {content.cancelText}
          </Button>
        </DialogClose>
        <Button
          variant={actions.confirmVariant ?? "destructive"}
          className={mergedClassNames.confirmButton}
          onClick={actions.onConfirm}
          disabled={actions.confirmDisabled ?? false}
          data-testid={confirmButtonTestId}
        >
          {actions.confirmLoading && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          {content.confirmText}
        </Button>
      </DialogFooter>
    </>
  );
};
